<script lang="ts" setup>
import { computed, onMounted, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { useIntervalFn } from '@vueuse/core'
import dayjs from 'dayjs'
import { sumBy } from 'lodash'
import axios from 'axios'

import { DashboardWidget, WidgetType } from '@/types/dashboard'
import {
    DispatcherTour,
    PreparedDispatcherTour,
} from '@/types/delivery-management-dispatcher-overview-widget'
import { TransportObjectType } from '@/types/transport-object'
import { Shipment, TourEvent, TourEventStatus } from '@/types/delivery-management'
import { differenceFromMinutes, localeTime } from '@/utils/dates'
import { useDashboardStore } from '@/stores/dashboard-store'
import { useAuthStore } from '@/stores/auth-store'
import {
    groupTourEvents,
    getShipmentTypeBadge,
} from '@/utils/delivery-management/group-tour-events'
import { destinationAddress } from '@/utils/delivery-management'
import { splitAddressString } from '@/utils/string'
import { ResourceResponse, uuid } from '@/types/general'
import { useMittListener } from '@/hooks/use-mitt-listener'

import DispatcherTourCard from '@/components/dashboard/widgets/delivery-management/DispatcherOverview/DispatcherTourCard.vue'
import DispatcherEventGroup from '@/components/dashboard/widgets/delivery-management/DispatcherOverview/DispatcherEventGroup.vue'
import DispatcherStopPopover from '@/components/dashboard/widgets/delivery-management/DispatcherOverview/DispatcherStopPopover.vue'
import MyWidget from '@/components/dashboard/widgets/MyWidget.vue'
import LoaderWrapper from '@/components/loaders/LoaderWrapper.vue'
import DeviationModal from '@/components/delivery-management/DeviationModal.vue'

interface Props {
    widget: DashboardWidget<WidgetType.DeliveryManagementDispatcherOverview>
    preview?: boolean
}

const props = defineProps<Props>()
const { t } = useI18n()
const authStore = useAuthStore()
const dashboardStore = useDashboardStore()

const dispatcherTours = ref<DispatcherTour[]>([])
const loading = ref(false)
const currentDeviationId = ref<uuid | null>(null)

const tours = computed<PreparedDispatcherTour[]>(() => {
    return dispatcherTours.value.map<PreparedDispatcherTour>((tour) => {
        const result = groupTourEvents<TourEvent<Shipment>>(tour.events)
        const firstEvent = tour.events[0]
        const lastEvent = tour.events[tour.events.length - 1]
        const nextEvent = tour.events.find((event) => event.handledAt === null) ?? lastEvent
        const delay =
            (nextEvent?.liveEstimatedAt ?? nextEvent?.estimatedAt)
                ? dayjs().diff(nextEvent?.estimatedAt, 'minutes')
                : 0
        const delayTooltip = t(delay > 0 ? 'delay' : 'aheadOfTime')

        const preparedTour: PreparedDispatcherTour = {
            ...tour,
            units: tour.transportObjects
                .filter((t) => t.type === TransportObjectType.Unit)
                .map((t) => t.registrationNumber)
                .join(', '),
            vehicles: tour.transportObjects
                .filter((t) => t.type === TransportObjectType.Vehicle)
                .map((t) => t.registrationNumber)
                .join(', '),
            hasVehicleIncidents: tour.incidents.some(
                (t) => t.transportObject.type === TransportObjectType.Vehicle,
            ),
            hasUnitIncidents: tour.incidents.some(
                (t) => t.transportObject.type === TransportObjectType.Unit,
            ),
            isTourStarted: tour.startedAt != null,
            startLabel: tour.startedAt
                ? `${localeTime(tour.startedAt)} (${localeTime(tour.plannedAt)})`
                : localeTime(tour.plannedAt),
            expectedEndLabel: tour.optimizationDetails?.duration
                ? localeTime(
                      dayjs(tour.startedAt ?? tour.plannedAt).add(
                          tour.optimizationDetails!.duration,
                          'seconds',
                      ),
                  )
                : null,
            endLabel: tour.endedAt ? localeTime(tour.endedAt) : null,
            estimatedDrivingTime: differenceFromMinutes(
                tour.events.length > 1 && lastEvent?.estimatedDepartureAt && firstEvent?.estimatedAt
                    ? dayjs(lastEvent.estimatedDepartureAt).diff(
                          dayjs(firstEvent.estimatedAt),
                          'minutes',
                      )
                    : 0,
            ),
            currentDrivingTime: tour.startedAt
                ? differenceFromMinutes(dayjs().diff(dayjs(tour.startedAt), 'minutes'))
                : undefined,
            delayTooltip,
            delay: delay,
            delayText: differenceFromMinutes(Math.abs(delay)),
            eventGroups: result.groups.map((events) => {
                const firstEvent = events[0]
                const reportEvent = tour.reportEvents?.find((event) =>
                    events.some((e) => event.tourEventIds.includes(e.id)),
                )
                const address = splitAddressString(destinationAddress(events[0].shipment!))

                const allShipmentsHandled =
                    reportEvent?.startedAt != null && reportEvent?.endedAt != null
                const anyShipmentStarted = reportEvent?.startedAt != null

                const status: TourEventStatus = allShipmentsHandled
                    ? TourEventStatus.Done
                    : anyShipmentStarted
                      ? TourEventStatus.Current
                      : TourEventStatus.Planned

                const hasUnsuccessful = events.some((e) => e.shipment?.unsuccessful)
                const hasDeviation = events.some((e) => (e.shipment?.deviations.length ?? 0) > 0)
                const packagingProductsDelivered = sumBy(events, 'packagingProductsCount')
                const packagingProductsTransactions = events.reduce((sum, ev) => {
                    const shipmentProducts =
                        ev.shipment?.products?.reduce((pSum: number, p) => {
                            return pSum + (p.delivered || 0)
                        }, 0) || 0
                    return sum + shipmentProducts
                }, 0)
                const hasPackagingMismatch =
                    events.some(
                        (ev) => ev.shipment?.products && ev.shipment?.products.length > 0,
                    ) &&
                    events.some((ev) =>
                        ev.transactions?.some((t) => (t.products?.length ?? 0) > 0),
                    ) &&
                    packagingProductsDelivered !== packagingProductsTransactions

                const estimatedTime = firstEvent.liveEstimatedAt ?? firstEvent.estimatedAt
                return {
                    id: events.map((e) => e.id).join('-'),
                    address: address.address,
                    zipcodeCity: `${address.zipcode} ${address.city}`,
                    reportEvent: reportEvent || null,
                    eta: estimatedTime ? localeTime(estimatedTime) : null,
                    plannedAt:
                        localeTime(firstEvent.estimatedAt || firstEvent.shipment?.plannedAt) ?? '',
                    status,
                    shipmentsTotal: events.length,
                    shipmentsDone: events.filter((e) => e.handledAt).length,
                    shipmentCustomers: firstEvent.shipment?.receiverName,
                    shipment: firstEvent.shipment!,
                    stopDelayLabel: (() => {
                        if (!estimatedTime) return
                        const delayMinutes = dayjs().diff(dayjs(estimatedTime), 'minute')
                        if (delayMinutes !== 0) {
                            const prefix = delayMinutes > 0 ? t('delay') : t('early')
                            const time = differenceFromMinutes(Math.abs(delayMinutes))
                            return `${prefix} ${time}`
                        }

                        return ''
                    })(),
                    packagingProductsCount: packagingProductsDelivered,
                    driverEstimatedTimeAt: firstEvent.driverEstimatedAt
                        ? firstEvent.driverEstimatedAt
                        : undefined,
                    hasUnsuccessful,
                    hasDeviation,
                    hasPackagingMismatch,
                    events: events.map((event) => ({
                        ...event,
                        shipment: {
                            ...event.shipment!,
                            typeBadge: getShipmentTypeBadge(event.shipment!.type),
                        },
                    })),
                }
            }),
        }
        return preparedTour
    })
})
async function fetchDispatcherTours() {
    // Avoid fetching if it's already in progress
    if (loading.value) {
        return
    }

    const dashboardId = dashboardStore.currentDashboard?.id
    if (!dashboardId) {
        return
    }

    loading.value = true

    try {
        const response = await axios.get<ResourceResponse<DispatcherTour[]>>(
            window.route('dashboards.dm-dispatcher-overview', {
                dashboard: dashboardId,
                shareToken: !authStore.user
                    ? (dashboardStore.currentDashboard?.shareToken ?? null)
                    : null,
                'per-page': 100,
            }),
        )

        dispatcherTours.value = response.data.data
    } finally {
        loading.value = false
    }
}

function showDeviationModal(deviationId: uuid) {
    currentDeviationId.value = deviationId
}

function hideDeviationModal() {
    currentDeviationId.value = null
}

useMittListener('openDeviationModal', showDeviationModal)
// Auto-refresh every minute for live data
useIntervalFn(fetchDispatcherTours, 1_000 * 60)

onMounted(() => fetchDispatcherTours())
</script>

<template>
    <MyWidget :widget="props.widget" :preview="props.preview">
        <LoaderWrapper :visible="loading" />
        <div class="flex flex-col h-full gap-4">
            <div
                v-if="tours.length === 0 && !loading"
                class="flex flex-col items-center justify-center py-12 text-stone-500 h-full min-h-[400px]"
            >
                <mdi:truck-outline class="w-12 h-12 mb-4 text-stone-900 dark:text-stone-100" />
                <h3
                    class="text-lg font-medium text-stone-900 dark:text-stone-100 mb-2"
                    v-text="t('noToursAvailable')"
                />
                <p class="text-sm text-center max-w-sm" v-text="t('noToursAvailableDescription')" />
            </div>

            <div v-for="tour in tours" v-else :key="tour.id" class="w-full flex items-stretch h-36">
                <DispatcherTourCard :tour="tour" />
                <div
                    class="flex-1 overflow-x-scroll flex items-stretch min-w-0 w-full gap-[3px] pr-[1px] relative z-0"
                >
                    <DispatcherStopPopover
                        v-for="(eventGroup, index) in tour.eventGroups"
                        :key="eventGroup.id"
                        :event-group="eventGroup"
                        :tour-name="tour.name"
                    >
                        <DispatcherEventGroup
                            :event-group="eventGroup"
                            :is-tour-started="tour.isTourStarted"
                            :index="index"
                            :event-groups-count="tour.eventGroups.length"
                        />
                    </DispatcherStopPopover>
                </div>
            </div>
        </div>
    </MyWidget>
    <DeviationModal
        v-if="currentDeviationId"
        :id="currentDeviationId"
        @update:open="hideDeviationModal"
    />
</template>
